/**
 * Copyright © 2026 Adnuntius AS.
 */
import {
  AfterViewInit,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {defaultKeymap, history, historyKeymap} from '@codemirror/commands';
import {html} from '@codemirror/lang-html';
import {Compartment, EditorState, Extension} from '@codemirror/state';
import {EditorView, highlightActiveLine, highlightActiveLineGutter, keymap, lineNumbers} from '@codemirror/view';
import {cm5Default} from './codemirror-cm5-default-theme';
import {APP_SHARED} from '../app-shared';

@Component({
  standalone: true,
  selector: 'adn-codemirror-field',
  imports: [...APP_SHARED],
  template: `
    <div #editorHost class="codemirror-field"></div>
    <textarea
      *ngIf="fieldName"
      #hiddenTextarea
      [attr.name]="fieldName"
      hidden
      tabindex="-1"
      aria-hidden="true"></textarea>
  `
})
export class CodemirrorFieldComponent implements AfterViewInit, DoCheck, OnChanges, OnDestroy {
  @Input() model: any;
  @Input() modelProperty: string;
  @Input() fieldName: string;
  @Input() mode = 'htmlmixed';
  @Input() readOnly = false;
  @Input() codemirrorOpts: Record<string, any> = {};
  @Input() refreshTrigger: any;

  @Output() fieldChange = new EventEmitter<void>();

  @ViewChild('editorHost', {static: true}) editorHost: ElementRef<HTMLDivElement>;
  @ViewChild('hiddenTextarea') hiddenTextarea: ElementRef<HTMLTextAreaElement>;

  private view: EditorView | null = null;
  private readonly readOnlyCompartment = new Compartment();
  private readonly highlightLineCompartment = new Compartment();
  private viewInitialized = false;
  private syncingFromEditor = false;
  private lastObservedModelValue = '';

  constructor() {
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.initEditor();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.readOnly && this.view) {
      this.setReadOnly(this.isReadOnly());
    }

    if (changes.refreshTrigger && this.view && !changes.refreshTrigger.firstChange) {
      this.queueRefresh();
    }

    if (changes.model && this.model) {
      if (this.view) {
        this.syncEditorValue();
      } else if (this.viewInitialized) {
        this.initEditor();
      }
    }

    if (this.hasStructuralChange(changes)) {
      this.destroyEditor();
      if (this.viewInitialized && this.model) {
        this.initEditor();
      }
    }
  }

  ngDoCheck(): void {
    if (!this.view || this.syncingFromEditor) {
      return;
    }

    const value = this.getModelValue();
    if (value !== this.lastObservedModelValue) {
      this.lastObservedModelValue = value;
      this.syncEditorValue(false);
    }
  }

  ngOnDestroy(): void {
    this.destroyEditor();
  }

  private hasStructuralChange(changes: SimpleChanges): boolean {
    if (changes.modelProperty || changes.fieldName || changes.mode) {
      return true;
    }
    if (!changes.codemirrorOpts) {
      return false;
    }
    const previous = changes.codemirrorOpts.previousValue || {};
    const current = changes.codemirrorOpts.currentValue || {};
    return previous.mode !== current.mode
      || previous.lineNumbers !== current.lineNumbers
      || previous.readOnly !== current.readOnly
      || previous.highlightActiveLine !== current.highlightActiveLine
      || previous.smartIndent !== current.smartIndent;
  }

  private initEditor(): void {
    if (this.view || !this.model || !this.modelProperty || !this.fieldName) {
      return;
    }

    const host = this.editorHost.nativeElement;
    host.innerHTML = '';

    const state = EditorState.create({
      doc: this.getModelValue(),
      extensions: this.buildExtensions()
    });

    this.view = new EditorView({
      state,
      parent: host
    });

    this.lastObservedModelValue = this.getModelValue();
    this.syncHiddenTextarea(this.getModelValue());
    this.queueRefresh();
  }

  private buildExtensions(): Extension[] {
    const extensions: Extension[] = [
      history(),
      keymap.of([...defaultKeymap, ...historyKeymap]),
      this.getLanguageExtension(),
      cm5Default,
      EditorView.lineWrapping,
      this.readOnlyCompartment.of(this.getReadOnlyExtensions()),
      EditorView.updateListener.of((update) => {
        if (update.docChanged && !this.syncingFromEditor) {
          this.onEditorChange(update.state.doc.toString());
        }
      })
    ];

    if (this.shouldShowLineNumbers()) {
      extensions.push(lineNumbers());
    }

    if (this.shouldHighlightActiveLine()) {
      extensions.push(
        this.highlightLineCompartment.of([]),
        EditorView.focusChangeEffect.of((_state, focusing) =>
          this.highlightLineCompartment.reconfigure(
            focusing ? [highlightActiveLine(), highlightActiveLineGutter()] : []
          )
        )
      );
    }

    return extensions;
  }

  private getLanguageExtension(): Extension {
    const mode = this.codemirrorOpts?.mode || this.mode;
    if (mode === 'htmlmixed' || mode === 'html') {
      return html();
    }
    return html();
  }

  private shouldShowLineNumbers(): boolean {
    return this.codemirrorOpts?.lineNumbers !== false;
  }

  private shouldHighlightActiveLine(): boolean {
    return this.codemirrorOpts?.highlightActiveLine !== false;
  }

  private isReadOnly(): boolean {
    return this.readOnly || this.codemirrorOpts?.readOnly === true;
  }

  private getReadOnlyExtensions(): Extension[] {
    if (!this.isReadOnly()) {
      return [];
    }
    return [EditorState.readOnly.of(true), EditorView.editable.of(false)];
  }

  private setReadOnly(readOnly: boolean): void {
    if (!this.view) {
      return;
    }
    this.view.dispatch({
      effects: this.readOnlyCompartment.reconfigure(readOnly ? this.getReadOnlyExtensions() : [])
    });
  }

  private onEditorChange(value: string): void {
    if (!this.view || !this.model || !this.modelProperty || this.syncingFromEditor) {
      return;
    }
    if (this.model[this.modelProperty] !== value) {
      this.model[this.modelProperty] = value;
      this.lastObservedModelValue = value;
      this.syncHiddenTextarea(value);
      this.fieldChange.emit();
    }
  }

  private getModelValue(): string {
    if (!this.model || !this.modelProperty) {
      return '';
    }
    const value = this.model[this.modelProperty];
    return value === null || value === undefined ? '' : String(value);
  }

  private syncEditorValue(refresh = true): void {
    if (!this.view) {
      return;
    }
    const value = this.getModelValue();
    if (this.view.state.doc.toString() === value) {
      this.syncHiddenTextarea(value);
      if (refresh) {
        this.queueRefresh();
      }
      return;
    }
    this.syncingFromEditor = true;
    this.view.dispatch({
      changes: {from: 0, to: this.view.state.doc.length, insert: value}
    });
    this.syncingFromEditor = false;
    this.lastObservedModelValue = value;
    this.syncHiddenTextarea(value);
    if (refresh) {
      this.queueRefresh();
    }
  }

  private syncHiddenTextarea(value: string): void {
    const textarea = this.hiddenTextarea?.nativeElement;
    if (textarea) {
      textarea.value = value;
    }
  }

  private queueRefresh(): void {
    setTimeout(() => this.view?.requestMeasure(), 0);
  }

  private destroyEditor(): void {
    if (!this.view) {
      return;
    }
    this.view.destroy();
    this.view = null;
    this.lastObservedModelValue = '';
    this.editorHost.nativeElement.innerHTML = '';
  }
}
